import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Status } from "@/gen/rbt/cloud/v1alpha1/application/application_pb";
import { useApplication } from "@/gen/rbt/cloud/v1alpha1/application/application_rbt_react";
import { useOrganization } from "@/gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import {
  Check,
  CircleHelp,
  Copy,
  ExternalLink,
  Pin,
  PinOff,
} from "lucide-react";
import { Suspense, useState } from "react";
import { ApplicationCardSkeleton } from "./applications/ApplicationCardSkeleton";
import { EmptyState } from "./applications/EmptyState";
import { MetricsDisplay } from "./applications/MetricsDisplay";
import { RevisionTimeline, StatusBadge } from "./applications/RevisionTimeline";
import { useAuthenticatedContext } from "./AuthenticatedLayout";

/**
 * Construct the application URL from an application ID. Mirrors the
 * Python `_application_url()` in `reboot/cli/cloud.py`.
 *
 * Applications are served under the cluster's own domain, which the
 * Cloud dashboard's same-site `VITE_APP_REBOOT_ENDPOINT` may not be.
 */
function getApplicationUrl(applicationId: string): string {
  const cloudUrl =
    import.meta.env.VITE_APP_CLUSTER_ENDPOINT ||
    import.meta.env.VITE_APP_REBOOT_ENDPOINT ||
    "http://localhost:9991";
  const [protocol, hostnamePort] = cloudUrl.split("://", 2);
  const cellHostnamePort = hostnamePort.replace(/^cloud\./, "");
  return `${protocol}://${applicationId}.${cellHostnamePort}`;
}

function HelpIcon({ tooltip }: { tooltip: string }) {
  return (
    <button
      type="button"
      className="relative shrink-0 cursor-help group"
      aria-label={tooltip}
    >
      <CircleHelp className="h-3.5 w-3.5 text-muted-foreground" />
      <span
        role="tooltip"
        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block group-focus:block w-48 px-2 py-1 rounded bg-foreground text-background text-xs text-center leading-snug z-10"
      >
        {tooltip}
      </span>
    </button>
  );
}

function CopyButton({ url, label }: { url: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="p-0.5 rounded hover:bg-muted transition-colors shrink-0"
      aria-label={`Copy ${label} URL`}
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
      )}
    </button>
  );
}

function AppUrls({ appUrl }: { appUrl: string }) {
  const mcpUrl = `${appUrl}/mcp`;
  return (
    <div className="space-y-1 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-medium text-muted-foreground shrink-0 w-8">
          API
        </span>
        <HelpIcon tooltip="Use this URL in your application's .env file to connect your full-stack app." />
        <code className="bg-muted px-1.5 py-0.5 rounded truncate">
          {appUrl}
        </code>
        <CopyButton url={appUrl} label="API" />
      </div>
      <div className="flex items-center gap-2">
        <span className="font-medium text-muted-foreground shrink-0 w-8">
          MCP
        </span>
        <HelpIcon tooltip="Use this URL to connect an MCP client (e.g. Claude, ChatGPT, VS Code)." />
        <code className="bg-muted px-1.5 py-0.5 rounded truncate">/mcp</code>
        <CopyButton url={mcpUrl} label="MCP" />
      </div>
    </div>
  );
}

export function ApplicationsPage() {
  const { currentOrgId } = useAuthenticatedContext();

  return (
    <>
      {/* Page header. */}
      <div className="flex items-center justify-between mb-6 pl-6">
        <h1 className="text-3xl font-bold">Applications</h1>
        <Button asChild>
          <a
            href="https://docs.reboot.dev/deploy_on_reboot_cloud"
            target="_blank"
            rel="noopener noreferrer"
          >
            How to launch an application
            <ExternalLink className="h-4 w-4 ml-2" />
          </a>
        </Button>
      </div>

      {/* Content. */}
      <div>
        {currentOrgId ? (
          <Suspense
            key={currentOrgId}
            fallback={
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <ApplicationCardSkeleton key={i} />
                ))}
              </div>
            }
          >
            <ApplicationsList orgId={currentOrgId} />
          </Suspense>
        ) : (
          <EmptyState />
        )}
      </div>
    </>
  );
}

function ApplicationsList({ orgId }: { orgId: string }) {
  const {
    useListApplications,
    useGetMembers,
    pinApplication,
    unpinApplication,
  } = useOrganization({ id: orgId });
  const { response } = useListApplications({}, { suspense: true });
  const { response: membersResponse } = useGetMembers({}, { suspense: true });
  const applications = response?.applications ?? [];
  const pinnedIds = new Set(response?.pinnedQualifiedApplicationNames ?? []);

  // Build a userId -> email lookup map.
  const userEmails: Record<string, string> = {};
  if (membersResponse?.members) {
    for (const [userId, member] of Object.entries(membersResponse.members)) {
      if (member.email) {
        userEmails[userId] = member.email;
      }
    }
  }

  if (applications.length === 0) {
    return <EmptyState />;
  }

  // Split into pinned and unpinned, preserving server order.
  const pinnedApps = (response?.pinnedQualifiedApplicationNames ?? [])
    .map((id) =>
      applications.find((app) => app.qualifiedApplicationName === id)
    )
    .filter(Boolean);
  const unpinnedApps = applications.filter(
    (app) => !pinnedIds.has(app.qualifiedApplicationName)
  );

  const handleTogglePin = async (
    qualifiedApplicationName: string,
    isPinned: boolean
  ) => {
    if (isPinned) {
      await unpinApplication({ qualifiedApplicationName });
    } else {
      await pinApplication({ qualifiedApplicationName });
    }
  };

  return (
    <div className="space-y-4">
      {pinnedApps.length > 0 && (
        <>
          <h2 className="text-sm font-medium text-muted-foreground mb-3 pl-6">
            Pinned
          </h2>
          {pinnedApps.map((app) => (
            <Suspense
              key={app!.qualifiedApplicationName}
              fallback={<ApplicationCardSkeleton />}
            >
              <ApplicationDetailCard
                qualifiedApplicationName={app!.qualifiedApplicationName}
                applicationName={app!.applicationName}
                userEmails={userEmails}
                isPinned={true}
                onTogglePin={() =>
                  handleTogglePin(app!.qualifiedApplicationName, true)
                }
              />
            </Suspense>
          ))}
        </>
      )}
      <h2 className="text-sm font-medium text-muted-foreground mb-3 pl-6">
        Applications
      </h2>
      {unpinnedApps.map((app) => (
        <Suspense
          key={app.qualifiedApplicationName}
          fallback={<ApplicationCardSkeleton />}
        >
          <ApplicationDetailCard
            qualifiedApplicationName={app.qualifiedApplicationName}
            applicationName={app.applicationName}
            userEmails={userEmails}
            isPinned={false}
            onTogglePin={() =>
              handleTogglePin(app.qualifiedApplicationName, false)
            }
          />
        </Suspense>
      ))}
    </div>
  );
}

function ApplicationDetailCard({
  qualifiedApplicationName,
  applicationName,
  userEmails,
  isPinned,
  onTogglePin,
}: {
  qualifiedApplicationName: string;
  applicationName: string;
  userEmails: Record<string, string>;
  isPinned: boolean;
  onTogglePin: () => void;
}) {
  const { useGet } = useApplication({ id: qualifiedApplicationName });
  const { response } = useGet({}, { suspense: true });

  const status = response?.status;
  const revisions = response?.revisions ?? [];
  const applicationId = response?.applicationId;
  const appUrl = applicationId ? getApplicationUrl(applicationId) : undefined;

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="py-5 pl-6 pr-5">
        {/* Header: name + status badge + inspect + pin. */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg truncate min-w-0">
            {applicationName}
          </h3>
          <div className="flex items-center gap-2">
            {status != null && <StatusBadge status={status} />}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onTogglePin}
              title={isPinned ? "Unpin application" : "Pin application"}
            >
              {isPinned ? (
                <PinOff className="h-4 w-4" />
              ) : (
                <Pin className="h-4 w-4" />
              )}
            </Button>
            {status === Status.UP && appUrl && (
              <Button variant="outline" size="sm" className="h-7" asChild>
                <a
                  href={`${appUrl}/__/inspect`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View State
                  <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* App URLs (only when the application is up). */}
        {status === Status.UP && appUrl && (
          <div className="mb-4">
            <AppUrls appUrl={appUrl} />
          </div>
        )}

        {/* Revision history (left) + metrics (right). */}
        <div className="flex flex-col-reverse md:flex-row md:gap-6">
          {revisions.length > 0 && (
            <div className="min-w-0 flex-1">
              <RevisionTimeline revisions={revisions} userEmails={userEmails} />
            </div>
          )}
          <div className="mb-3 md:mb-0 md:ml-auto shrink-0 space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">
              Performance
            </h4>
            <MetricsDisplay metrics={response?.metrics} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
